import { Button, ModalTitle } from "@kaltura/ds-react-components";
import { baseUrl, getDataFromKms, translate } from "@kaltura/mediaspace-shared-utils";
import { useRef, useState } from "react";
import { StyledModalActions, StyledModalContent } from "./StyledComponents";
import ImportChannelList from "./import-channel-list/ImportChannelList";
import Message from "./message/Message";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";

export type ImportChannelModalContentProps = {
    onModalClose: () => void;
    channels: Record<string, string>[];
    url: string;
    channelId: number;
    errorMessage: string;
};

/**
 * Channel Import Channel action Modal content
 */
export const ImportChannelModalContent = (props: ImportChannelModalContentProps) => {
    const { onModalClose, url, channels, channelId, errorMessage } = props;

    const [selectedChannel, setSelectedChannel] = useState<string | undefined>();
    const [processing, setProcessing] = useState<boolean>(false);

    const status = useRef("");
    const { showToast } = useToastsContext();

    const importStatus = () => {
        getDataFromKms(
            `${baseUrl}/importchannel/index/import-status`,
            { token: `${selectedChannel}-${channelId}` },
            (statusResult) => {
                status.current = statusResult.status;
            },
            false,
            onError
        );
    };

    const onImportStart = () => {
        status.current = "started";
        const interval = setInterval(
            () => {
                if (status.current === "failed") {
                    clearInterval(interval);
                    onError();
                }
                else if (status.current === "done") {
                    clearInterval(interval);
                    onImportDone();
                }
                else {
                    importStatus();
                }
            },
            5000
        );
        setTimeout(
            () => {
                onModalClose();
            },
            2000
        );
    };

    const onImportDone = () => {
        showToast({
            severity: "success",
            message: translate("Importing completed successfully, Please refresh the page for updated list."),
        });
    };

    const onError = () => {
        onModalClose();
        showToast({ severity: "error", message: translate("Error Importing") });
    };

    const onImportRequest = () => {
        setProcessing(true);
        getDataFromKms(
            `${baseUrl}${url}`,
            {
                sourceId: selectedChannel,
                targetId: channelId,
            },
            onImportStart,
            false,
            onError
        );
    };

    const onClose = () => {
        onModalClose();
    };

    return (
        <>
            {!errorMessage && !processing && (
                <ModalTitle>{translate("Select the channel you want to import")}</ModalTitle>
            )}
            <StyledModalContent>
                {errorMessage && <Message text={errorMessage} />}
                {!errorMessage && processing && (
                    <Message
                        showTitle={true}
                        text={translate("The import process has started, a notification will appear once completed.")}
                    />
                )}
                {!errorMessage && !processing && (
                    <ImportChannelList
                        channels={channels}
                        selectedChannel={selectedChannel}
                        onChannelSelect={setSelectedChannel}
                    />
                )}
            </StyledModalContent>
            <StyledModalActions>
                <Button
                    aria-label={translate("Cancel")}
                    onClick={onClose}
                    key={"cancel"}
                    variant={"borderless"}
                    color={"secondary"}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    aria-label={translate("Import")}
                    key={"create"}
                    onClick={onImportRequest}
                    disabled={!selectedChannel}
                    loading={processing}
                >
                    {translate("Import")}
                </Button>
            </StyledModalActions>
        </>
    );
};
